/**
 * Storage Manager
 *
 * Centralized AsyncStorage wrapper with typed accessors.
 * Provides consistent JSON parsing/stringifying and error handling.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

import { AUTH_KEYS, ORG_KEYS, REPORT_CACHE_KEYS, UI_PREFERENCES_KEYS, LOGOUT_KEYS } from './keys';

/**
 * Get a string value from storage
 * @param {string} key - Storage key
 * @returns {Promise<string|null>} Value or null
 */
export async function getString(key) {
  try {
    return await AsyncStorage.getItem(key);
  } catch (error) {
    console.warn(`[Storage] Failed to get ${key}:`, error);
    return null;
  }
}

/**
 * Set a string value in storage
 * @param {string} key - Storage key
 * @param {string} value - Value to store
 */
export async function setString(key, value) {
  try {
    await AsyncStorage.setItem(key, value);
  } catch (error) {
    console.warn(`[Storage] Failed to set ${key}:`, error);
  }
}

/**
 * Get a JSON value from storage
 * @param {string} key - Storage key
 * @param {*} defaultValue - Default value if not found or parse fails
 * @returns {Promise<*>} Parsed value or default
 */
export async function getJSON(key, defaultValue = null) {
  try {
    const value = await AsyncStorage.getItem(key);
    if (value === null) return defaultValue;
    return JSON.parse(value);
  } catch (error) {
    console.warn(`[Storage] Failed to get/parse ${key}:`, error);
    return defaultValue;
  }
}

/**
 * Set a JSON value in storage
 * @param {string} key - Storage key
 * @param {*} value - Value to store (will be stringified)
 */
export async function setJSON(key, value) {
  try {
    await AsyncStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`[Storage] Failed to set ${key}:`, error);
  }
}

/**
 * Remove a key from storage
 * @param {string} key - Storage key
 */
export async function remove(key) {
  try {
    await AsyncStorage.removeItem(key);
  } catch (error) {
    console.warn(`[Storage] Failed to remove ${key}:`, error);
  }
}

/**
 * Remove multiple keys from storage
 * @param {string[]} keys - Array of keys to remove
 */
export async function removeMultiple(keys) {
  try {
    await AsyncStorage.multiRemove(keys);
  } catch (error) {
    console.warn(`[Storage] Failed to remove keys:`, error);
  }
}

// ============================================
// Auth Storage Helpers
// ============================================

/**
 * Get auth token
 * @returns {Promise<string|null>}
 */
export async function getToken() {
  return getString(AUTH_KEYS.TOKEN);
}

/**
 * Remove auth token
 */
export async function removeToken() {
  return remove(AUTH_KEYS.TOKEN);
}

/**
 * Get login method
 * @returns {Promise<'login'|'qr'|null>}
 */
export async function getLoginMethod() {
  return getString(AUTH_KEYS.METHOD);
}

/**
 * Set login method
 * @param {'login'|'qr'} method
 */
export async function setLoginMethod(method) {
  return setString(AUTH_KEYS.METHOD, method);
}

/**
 * Check if user is admin
 * @returns {Promise<boolean>}
 */
export async function getIsAdmin() {
  const value = await getString(AUTH_KEYS.IS_ADMIN);
  return value === 'true';
}

/**
 * Set admin status
 * @param {boolean} isAdmin
 */
export async function setIsAdmin(isAdmin) {
  return setString(AUTH_KEYS.IS_ADMIN, isAdmin ? 'true' : 'false');
}

/**
 * Check if user has user management permission
 * @returns {Promise<boolean>}
 */
export async function getIsUserAdmin() {
  const value = await getString(AUTH_KEYS.IS_USER_ADMIN);
  return value === 'true';
}

/**
 * Set user admin status
 * @param {boolean} isUserAdmin
 */
export async function setIsUserAdmin(isUserAdmin) {
  return setString(AUTH_KEYS.IS_USER_ADMIN, isUserAdmin ? 'true' : 'false');
}

/**
 * Check if user has organization management permission
 * @returns {Promise<boolean>}
 */
export async function getIsOrgAdmin() {
  const value = await getString(AUTH_KEYS.IS_ORG_ADMIN);
  return value === 'true';
}

/**
 * Set org admin status
 * @param {boolean} isOrgAdmin
 */
export async function setIsOrgAdmin(isOrgAdmin) {
  return setString(AUTH_KEYS.IS_ORG_ADMIN, isOrgAdmin ? 'true' : 'false');
}

/**
 * Check if user is an account owner (can assign admin roles)
 * @returns {Promise<boolean>}
 */
export async function getIsAccountOwner() {
  const value = await getString(AUTH_KEYS.IS_ACCOUNT_OWNER);
  return value === 'true';
}

/**
 * Set account owner status
 * @param {boolean} isAccountOwner
 */
export async function setIsAccountOwner(isAccountOwner) {
  return setString(AUTH_KEYS.IS_ACCOUNT_OWNER, isAccountOwner ? 'true' : 'false');
}

/**
 * Check if user is a Moneta admin (super-admin with global access)
 * @returns {Promise<boolean>}
 */
export async function getIsMoneta() {
  const value = await getString(AUTH_KEYS.IS_MONETA);
  return value === 'true';
}

/**
 * Set Moneta admin status
 * @param {boolean} isMoneta
 */
export async function setIsMoneta(isMoneta) {
  return setString(AUTH_KEYS.IS_MONETA, isMoneta ? 'true' : 'false');
}

// ============================================
// Organization Storage Helpers
// ============================================

/**
 * Get current organization
 * @returns {Promise<string|null>}
 */
export async function getOrganization() {
  return getString(ORG_KEYS.ORGANIZATION);
}

/**
 * Set current organization
 * @param {string} organization
 */
export async function setOrganization(organization) {
  return setString(ORG_KEYS.ORGANIZATION, organization);
}

/**
 * Get organization access list
 * @returns {Promise<string[]>}
 */
export async function getOrganizationAccess() {
  const value = await getString(ORG_KEYS.ORGANIZATION_ACCESS);
  if (!value) return [];
  return value.split(',').filter(Boolean);
}

/**
 * Set organization access list
 * @param {string[]} organizations
 */
export async function setOrganizationAccess(organizations) {
  return setString(ORG_KEYS.ORGANIZATION_ACCESS, organizations.join(','));
}

/**
 * Clear organization access
 */
export async function clearOrganizationAccess() {
  return remove(ORG_KEYS.ORGANIZATION_ACCESS);
}

/**
 * Get routing data
 * @returns {Promise<Object|null>}
 */
export async function getRouting() {
  return getJSON(ORG_KEYS.ROUTING, null);
}

/**
 * Set routing data
 * @param {Object} routing
 */
export async function setRouting(routing) {
  return setJSON(ORG_KEYS.ROUTING, routing);
}

/**
 * Clear routing data
 */
export async function clearRouting() {
  return remove(ORG_KEYS.ROUTING);
}

/**
 * Get enabled feature keys for the active organization
 * @returns {Promise<string[]>}
 */
export async function getFeatures() {
  return getJSON(ORG_KEYS.FEATURES, []);
}

/**
 * Set enabled feature keys for the active organization
 * @param {string[]} features
 */
export async function setFeatures(features) {
  return setJSON(ORG_KEYS.FEATURES, features);
}

/**
 * Clear feature data
 */
export async function clearFeatures() {
  return remove(ORG_KEYS.FEATURES);
}

// ============================================
// Report Cache Helpers
// ============================================

/**
 * Get changed reports list
 * @returns {Promise<string[]>}
 */
export async function getChangedReports() {
  return getJSON(REPORT_CACHE_KEYS.REPORT_CHANGED, []);
}

/**
 * Add report to changed list
 * @param {string} uuid - Report UUID
 */
export async function addChangedReport(uuid) {
  const current = await getChangedReports();
  if (!current.includes(uuid)) {
    current.push(uuid);
    await setJSON(REPORT_CACHE_KEYS.REPORT_CHANGED, current);
  }
}

/**
 * Remove report from changed list
 * @param {string} uuid - Report UUID
 */
export async function removeChangedReport(uuid) {
  const current = await getChangedReports();
  const filtered = current.filter((id) => id !== uuid);
  await setJSON(REPORT_CACHE_KEYS.REPORT_CHANGED, filtered);
}

/**
 * Get commented reports cache
 * @returns {Promise<string[]>}
 */
export async function getCommentedReports() {
  return getJSON(REPORT_CACHE_KEYS.REPORT_COMMENTED, []);
}

/**
 * Get closed reports cache
 * @returns {Promise<string[]>}
 */
export async function getClosedReports() {
  return getJSON(REPORT_CACHE_KEYS.REPORT_CLOSED, []);
}

/**
 * Get reopened reports cache
 * @returns {Promise<string[]>}
 */
export async function getReopenedReports() {
  return getJSON(REPORT_CACHE_KEYS.REPORT_REOPENED, []);
}

// ============================================
// Session Management
// ============================================

/**
 * Clear all auth-related storage on logout
 */
export async function clearAuthStorage() {
  await removeMultiple(LOGOUT_KEYS);
}

/**
 * Store user session data after login
 * @param {Object} sessionData
 * @param {string[]} sessionData.organizationAccess
 * @param {boolean} sessionData.isAdmin
 * @param {boolean} sessionData.isUserAdmin
 * @param {boolean} sessionData.isOrgAdmin
 * @param {boolean} sessionData.isAccountOwner
 * @param {'login'|'qr'} sessionData.method
 */
export async function setSessionData({ organizationAccess, isAdmin, isUserAdmin, isOrgAdmin, isAccountOwner, method }) {
  await Promise.all([
    setOrganizationAccess(organizationAccess),
    setIsAdmin(isAdmin),
    setIsUserAdmin(isUserAdmin),
    setIsOrgAdmin(isOrgAdmin ?? false),
    setIsAccountOwner(isAccountOwner ?? false),
    setLoginMethod(method),
  ]);
}

// ============ UI Preference Helpers ============

/**
 * Get quiet mode preference (suppresses badge celebration modals)
 * @returns {Promise<boolean>}
 */
export async function getQuietMode() {
  return (await getString(UI_PREFERENCES_KEYS.QUIET_MODE)) === 'true';
}

/**
 * Set quiet mode preference
 * @param {boolean} enabled
 */
export async function setQuietMode(enabled) {
  await setString(UI_PREFERENCES_KEYS.QUIET_MODE, enabled.toString());
}

/**
 * Get the language override set in Settings.
 *
 * Returns 'system' when the user has never chosen, so the caller follows the
 * device language. Resolution lives in i18n/detectLocale.js.
 *
 * @returns {Promise<string>} 'en' | 'es' | 'system'
 */
export async function getLanguage() {
  return (await getString(UI_PREFERENCES_KEYS.LANGUAGE)) || 'system';
}

/**
 * Set the language override.
 * @param {string} language - 'en' | 'es' | 'system'
 */
export async function setLanguage(language) {
  await setString(UI_PREFERENCES_KEYS.LANGUAGE, language);
}

/**
 * Get persisted analytics collapsible-section state
 * @param {*} defaultValue - Returned if nothing is stored or parsing fails
 * @returns {Promise<Object|null>}
 */
export async function getAnalyticsSectionState(defaultValue = null) {
  return getJSON(UI_PREFERENCES_KEYS.ANALYTICS_SECTION_STATE, defaultValue);
}

/**
 * Set persisted analytics collapsible-section state
 * @param {Object} state
 */
export async function setAnalyticsSectionState(state) {
  await setJSON(UI_PREFERENCES_KEYS.ANALYTICS_SECTION_STATE, state);
}
